import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import Button from "@components/ui/Button";
import AlertBox from "@components/ui/AlertBox";
import TextInput from "@components/forms/TextInput";
import { useAuth } from "@contexts/authContext";
import { useRedirectParam } from "../../hooks/useRedirectParam";

function OtpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumeRedirect, getRedirect } = useRedirectParam();
  const { verifyOtp, resendOtp } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const [pageError, setPageError] = useState("");
  const [timer, setTimer] = useState(600); // 5 minutes
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState("");

  // Get email from sessionStorage on mount
  useEffect(() => {
    const storedEmail = sessionStorage.getItem("otpEmail");
    if (!storedEmail) {
      navigate("/login");
    } else {
      setEmail(storedEmail);
    }
  }, [navigate]);


  // Timer for resend functionality
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const redirectTo = searchParams.get("redirect");
  const hasSafeRedirect = redirectTo?.startsWith("/") && !redirectTo.startsWith("//");

  const onSubmit = async (data) => {
    setPageError("");
    try {
      const user = await verifyOtp({
        email,
        otp_code: data.otp,
      });

      toast.success("OTP verified successfully");

      // Redirect based on user type and profile completion
      // Use getRedirect() (non-destructive) for the profile check so the value
      // survives into the complete-profile flow if the user still needs it.
      const storedRedirect = getRedirect();
      if (!user.is_profile_complete) {
        return navigate("/complete-profile");
      } else if (storedRedirect) {
        consumeRedirect(); // consume only when we are actually using it
        return navigate(storedRedirect, { replace: true });
      } else if (hasSafeRedirect) {
        return navigate(redirectTo, { replace: true });
      } else if (user.is_admin) {
        return navigate("/admin", { replace: true });
      } else if (user.user_type === "lecturer") {
        return navigate("/lecturer/dashboard", { replace: true });
      } else {
        return navigate("/student/dashboard", { replace: true });
      }
    } catch (error) {
      setPageError(error);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setPageError("");
    try {
      await resendOtp({ email });
      toast.success("OTP sent to your email");
      setTimer(600); // Reset timer to 5 minutes
    } catch (error) {
      setPageError(error);
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="flex justify-center items-center min-h-[calc(100vh-100px)]">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8 mt-2">
          <h1 className="text-3xl font-bold">Verify Your Email</h1>
          <p className="mt-3 text-base-content/70">
            We've sent a verification code to <br />
            <span className="font-semibold">{email}</span>
          </p>
        </div>

        <div className="card bg-base-100 shadow-xl">
          <form onSubmit={handleSubmit(onSubmit)} className="card-body">
            <AlertBox message={pageError} type="error" />

            <TextInput
              label="Verification Code"
              name="otp"
              type="text"
              placeholder="000000"
              register={register}
              errors={errors}
              validationRules={{
                required: "Verification code is required",
                minLength: {
                  value: 6,
                  message: "Code must be 6 digits",
                },
                maxLength: {
                  value: 6,
                  message: "Code must be 6 digits",
                },
              }}
            />

            <Button isLoading={isSubmitting} className="w-full mt-4">
              Verify Email
            </Button>

            {/* Resend OTP Section */}
            <div className="text-center text-sm mt-6">
              {timer > 0 ? (
                <p className="text-base-content/70">
                  Resend code in{" "}
                  <span className="font-semibold text-primary">{formatTime(timer)}</span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                  className="link link-primary"
                >
                  {isResending ? "Sending..." : "Resend Code"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default OtpPage;